import { useAllColumns } from "@/composables/useAllColumns";
import useCustomFields from "@/composables/useCustomFields";
import { editPrintJob, getActiveJobs } from "@/services/printJobs.service";
import { useFilesStore } from "@/store/files.store";
import { useOrdersStore } from "@/store/orders.store";
import { PrintJob } from "@/types";
import { columnsToHydrateOptions } from "@/utils/orderItemsIncludes";
import { invalidatePrintJobScopes, PRINT_JOBS_QUERY_KEY, relocatePrintJobsInCache, scopeQueryKey } from "@/utils/printJobsCache";
import { collectPrintJobPreviewIds } from "@/utils/printJobs.utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/vue-query";
import { computed, MaybeRefOrGetter, onScopeDispose, ref, toValue, watch } from "vue";

export { patchPrintJobInCache } from "@/utils/printJobsCache";

type UseJobsOptions = {
  /** Fetch jobs assigned to this printer. */
  printerId?: MaybeRefOrGetter<number | null | undefined>;
  /** Fetch jobs that have no printer. */
  unassigned?: boolean;
};

function usePrintJobsHydrate() {
  const ordersStore = useOrdersStore();
  const { allColumns } = useAllColumns();

  return computed(() => {
    const sessionOrder = ordersStore.columnOrder;
    const order = Array.isArray(sessionOrder) ? sessionOrder : [];
    const sortedColumns = !order.length ? allColumns.value : allColumns.value.filter((col) => order.includes(col.field)).sort((a, b) => order.indexOf(a.field) - order.indexOf(b.field));
    return columnsToHydrateOptions(sortedColumns);
  });
}

function sortPrintJobs(jobs: PrintJob[]) {
  const active = jobs.filter((job) => !job.archivedAt && !job.endDate);
  return active.sort((a, b) => {
    if (!a.position && !b.position) return 0;
    if (!a.position) return 1;
    if (!b.position) return -1;
    if (a.position < b.position) return -1;
    if (a.position > b.position) return 1;
    return 0;
  });
}

function useEditPrintJobMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ jobId, data }: { jobId: number; data: Partial<PrintJob> }) => editPrintJob(jobId, data),
    onMutate: async ({ jobId, data: patch }) => {
      const previousQueries = queryClient.getQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY });
      const sourcePrinterIds: Array<number | null> = [];
      let existing: PrintJob | undefined;
      for (const [, rows] of previousQueries) {
        const match = rows?.find((job) => job.id === jobId);
        if (!match) continue;
        existing = match;
        sourcePrinterIds.push(match.printerId ?? null);
      }

      const scopes = [...sourcePrinterIds];
      if (patch.printerId !== undefined) scopes.push(patch.printerId ?? null);
      await Promise.all(scopes.map((printerId) => queryClient.cancelQueries({ queryKey: scopeQueryKey(printerId) })));

      if (patch.printerId !== undefined && existing) {
        relocatePrintJobsInCache(queryClient, [{ ...existing, ...patch, id: jobId }]);
      } else {
        queryClient.setQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY }, (old) => old?.map((job) => (job.id === jobId ? { ...job, ...patch } : job)));
      }

      return { previousQueries, sourcePrinterIds };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [key, previous] of context.previousQueries) {
          queryClient.setQueryData(key, previous);
        }
      }
    },
    onSettled: (_data, _err, { jobId, data: patch }, context) => {
      if (patch.position === undefined && patch.printerId === undefined) return;
      const targets = [...(context?.sourcePrinterIds ?? [])];
      if (patch.printerId !== undefined) {
        targets.push(patch.printerId ?? null);
      } else {
        const caches = queryClient.getQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY });
        for (const [, rows] of caches) {
          const match = rows?.find((job) => job.id === jobId);
          if (match) targets.push(match.printerId ?? null);
        }
      }
      invalidatePrintJobScopes(queryClient, targets);
    },
  });
}

/** Read whatever printer and unassigned slices are already cached. Does not fetch. */
export function useCachedPrintJobs() {
  const queryClient = useQueryClient();
  const editJobMutation = useEditPrintJobMutation();
  const data = ref<PrintJob[]>([]);

  function sync() {
    const caches = queryClient.getQueriesData<PrintJob[]>({ queryKey: PRINT_JOBS_QUERY_KEY });
    const byId = new Map<number, PrintJob>();
    for (const [, rows] of caches) {
      for (const job of rows ?? []) {
        if (job?.id != null) byId.set(job.id, job);
      }
    }
    data.value = [...byId.values()];
  }

  sync();
  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    const key = event.query.queryKey;
    if (Array.isArray(key) && key[0] === "printJobs") sync();
  });
  onScopeDispose(unsubscribe);

  const jobs = computed(() => sortPrintJobs(data.value));
  const unassignedJobs = computed(() => jobs.value.filter((job) => job.printerId == null));
  const assignedJobs = computed(() => jobs.value.filter((job) => job.printerId != null));

  return {
    jobs,
    unassignedJobs,
    assignedJobs,
    loading: computed(() => false),
    error: computed(() => null),
    editJobMutation,
  };
}

export default function useJobs(options: UseJobsOptions) {
  const hydrate = usePrintJobsHydrate();
  const { isLoading: customFieldsLoading } = useCustomFields();
  const filesStore = useFilesStore();
  const editJobMutation = useEditPrintJobMutation();
  const unassigned = options.unassigned === true;
  const printerId = computed(() => (unassigned ? null : toValue(options.printerId)));

  const enabled = computed(() => !customFieldsLoading.value && (unassigned || printerId.value != null));

  const {
    data,
    isLoading: jobsLoading,
    error,
  } = useQuery({
    enabled,
    queryKey: computed(() => {
      const hydrateKey = {
        includes: hydrate.value.includes,
        orderCustomFieldIds: hydrate.value.orderCustomFieldIds,
        orderItemCustomFieldIds: hydrate.value.orderItemCustomFieldIds,
      };
      if (unassigned) return ["printJobs", "unassigned", hydrateKey];
      if (printerId.value == null) return ["printJobs", "printer", "pending", hydrateKey];
      return ["printJobs", "printer", printerId.value, hydrateKey];
    }),
    queryFn: async () => {
      const scopePrinterId = unassigned ? null : printerId.value;
      if (!unassigned && scopePrinterId == null) return [];
      const list = await getActiveJobs("list", hydrate.value, scopePrinterId);
      const previewIds = collectPrintJobPreviewIds(list);
      if (previewIds.length) void filesStore.fetchPreviewUrls(previewIds);
      return list;
    },
    gcTime: 5 * 60 * 1000,
    staleTime: 5 * 60 * 1000,
  });

  const loading = computed(() => customFieldsLoading.value || jobsLoading.value);

  watch(
    data,
    (jobs) => {
      const previewIds = collectPrintJobPreviewIds(jobs);
      if (previewIds.length) void filesStore.fetchPreviewUrls(previewIds);
    },
    { flush: "post" },
  );

  const jobs = computed(() => sortPrintJobs(data.value ?? []));
  const unassignedJobs = computed(() => (unassigned ? jobs.value : jobs.value.filter((job) => job.printerId == null)));
  const assignedJobs = computed(() => jobs.value.filter((job) => job.printerId != null));

  return {
    jobs,
    unassignedJobs,
    assignedJobs,
    loading,
    error,
    editJobMutation,
  };
}
